import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { Button } from "components/ui/button";
import { Logo } from "components/logo";
import { Avatar, AvatarFallback, AvatarImage } from "components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "components/ui/dropdown-menu";
import {
  BadgeCheck,
  BriefcaseBusiness,
  ChevronDown,
  LayoutDashboard,
  Loader2,
  LogOut,
  Menu,
  Settings,
  X,
} from "lucide-react";
import { useState } from "react";
import { cn } from "lib/utils";
import {
  getAuthUserAvatarUrl,
  getAuthUserDisplayName,
  getAuthUserInitials,
  isAuthUserEmailVerified,
} from "lib/authUserDisplay";
import { selectAuthUser, selectIsAuthenticated } from "store/reducer";
import { useLogout } from "hooks/useLogout";

const navItems = [
  { label: "Offerings", href: "/opportunities", className: "d-none" },
  { label: "How It Works", href: "/how-it-works", className: "d-none" },
  { label: "List Your Company", href: "/raise", className: "d-none" },
  { label: "About", href: "/about", className: "d-none" },
];

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const user = useSelector(selectAuthUser);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const { logout, isLoggingOut } = useLogout();

  const displayName = getAuthUserDisplayName(user) || "User";
  const initials = getAuthUserInitials(user);
  const avatarImage = getAuthUserAvatarUrl(user);
  const emailVerified = isAuthUserEmailVerified(user);

  const handleLogout = async () => {
    setMobileMenuOpen(false);
    await logout();
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-backdrop-filter:bg-background/60">
      <div className="container mx-auto px-4 sm:px-6 lg:px-12 xl:px-16">
        <div className="flex h-16 items-center justify-between">
          <Link to="/" className="flex items-center">
            <Logo />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden items-center gap-8 md:flex">
            {navItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={cn(
                  "text-sm font-medium text-muted-foreground transition-colors hover:text-foreground",
                  item.className
                )}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          {/* Desktop Auth Buttons */}
          {isAuthenticated ? (
            <UserDropdown
              displayName={displayName}
              email={user?.email}
              emailVerified={emailVerified}
              initials={initials}
              image={avatarImage}
              onLogout={handleLogout}
              isLoggingOut={isLoggingOut}
            />
          ) : (
            <div className="hidden items-center gap-3 md:flex">
              <Button variant="ghost" asChild>
                <Link to="/sign-in">Log In</Link>
              </Button>
              <Button asChild>
                <Link to="/sign-up">Register</Link>
              </Button>
            </div>
          )}

          {/* Mobile Menu Button */}
          <button
            className="cursor-pointer rounded-md p-2 transition-colors hover:bg-muted md:hidden"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </button>
        </div>

        {/* Mobile Navigation */}
        <div
          className={cn(
            "overflow-hidden transition-all duration-300 ease-in-out md:hidden",
            mobileMenuOpen ? "max-h-96 pb-4" : "max-h-0"
          )}
        >
          <nav className="flex flex-col gap-2">
            {navItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={cn(
                  "rounded-md px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground",
                  item.className
                )}
                onClick={() => setMobileMenuOpen(false)}
              >
                {item.label}
              </Link>
            ))}

            {isAuthenticated ? (
              <MobileUserMenu
                displayName={displayName}
                email={user?.email}
                emailVerified={emailVerified}
                initials={initials}
                image={avatarImage}
                onNavigate={() => setMobileMenuOpen(false)}
                onLogout={handleLogout}
                isLoggingOut={isLoggingOut}
              />
            ) : (
              <div className="mt-2 flex flex-col gap-2 border-t pt-4">
                <Button variant="ghost" asChild className="justify-start">
                  <Link to="/sign-in" onClick={() => setMobileMenuOpen(false)}>
                    Log In
                  </Link>
                </Button>
                <Button asChild>
                  <Link to="/sign-up" onClick={() => setMobileMenuOpen(false)}>
                    Register
                  </Link>
                </Button>
              </div>
            )}
          </nav>
        </div>
      </div>
    </header>
  );
}

/* eslint-disable react/prop-types -- local presentational helpers. */
function EmailWithVerifiedBadge({ email, verified }) {
  if (!email) return null;
  return (
    <p className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
      <span className="truncate">{email}</span>
      {verified ? (
        <BadgeCheck
          className="h-3.5 w-3.5 shrink-0 text-emerald-600 dark:text-emerald-500"
          aria-label="Email verified"
          title="Email verified"
        />
      ) : null}
    </p>
  );
}

function UserDropdown({
  displayName,
  email,
  emailVerified,
  initials,
  image,
  onLogout,
  isLoggingOut,
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="hidden cursor-pointer items-center gap-2 rounded-full border px-2 py-1 text-sm font-medium transition-colors hover:bg-muted md:flex">
          <UserAvatar image={image} initials={initials} />
          <span className="max-w-32 truncate">{displayName}</span>
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        </button >
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="font-normal">
          <div className="flex items-center gap-3">
            <UserAvatar image={image} initials={initials} />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium leading-tight">
                {displayName}
              </p>
              <EmailWithVerifiedBadge email={email} verified={emailVerified} />
            </div>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild className="cursor-pointer">
          <Link to="/dashboard">
            <LayoutDashboard className="h-4 w-4" />
            Dashboard
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild className="cursor-pointer">
          <Link to="/my-investments">
            <BriefcaseBusiness className="h-4 w-4" />
            My Investments
          </Link>
        </DropdownMenuItem>
        {/* <DropdownMenuItem asChild className="cursor-pointer">
          <Link to="/portfolio">
            <BriefcaseBusiness className="h-4 w-4" />
            Portfolio
          </Link>
        </DropdownMenuItem> */}
        <DropdownMenuItem asChild className="cursor-pointer">
          <Link to="/settings">
            <Settings className="h-4 w-4" />
            Settings
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="cursor-pointer text-destructive focus:text-destructive"
          onClick={onLogout}
          disabled={isLoggingOut}
        >
          {isLoggingOut ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <LogOut className="h-4 w-4" />
          )}
          {isLoggingOut ? "Logging out..." : "Logout"}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function MobileUserMenu({
  displayName,
  email,
  emailVerified,
  initials,
  image,
  onNavigate,
  onLogout,
  isLoggingOut,
}) {
  return (
    <div className="mt-2 flex flex-col gap-2 border-t pt-4">
      <div className="flex items-center gap-3 rounded-md bg-muted/60 px-4 py-3">
        <UserAvatar image={image} initials={initials} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium leading-tight">
            {displayName}
          </p>
          <EmailWithVerifiedBadge email={email} verified={emailVerified} />
        </div>
      </div>
      <MobileAccountLink to="/dashboard" onClick={onNavigate}>
        <LayoutDashboard className="h-4 w-4" />
        Dashboard
      </MobileAccountLink>
      {/* <MobileAccountLink to="/portfolio" onClick={onNavigate}>
        <BriefcaseBusiness className="h-4 w-4" />
        Portfolio
      </MobileAccountLink> */}
      <MobileAccountLink to="/settings" onClick={onNavigate}>
        <Settings className="h-4 w-4" />
        Settings
      </MobileAccountLink>
      <button
        type="button"
        className="flex cursor-pointer items-center gap-2 rounded-md px-4 py-2 text-sm font-medium text-destructive transition-colors hover:bg-muted"
        onClick={onLogout}
        disabled={isLoggingOut}
      >
        {isLoggingOut ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <LogOut className="h-4 w-4" />
        )}
        {isLoggingOut ? "Logging out..." : "Logout"}
      </button>
    </div>
  );
}

function MobileAccountLink({ to, onClick, children }) {
  return (
    <Link
      to={to}
      className="flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
      onClick={onClick}
    >
      {children}
    </Link>
  );
}

function UserAvatar({ image, initials }) {
  return (
    <Avatar className="h-8 w-8">
      <AvatarImage src={image || undefined} alt="" key={image || "no-avatar"} />
      <AvatarFallback className="bg-primary text-xs font-semibold text-primary-foreground">
        {initials}
      </AvatarFallback>
    </Avatar>
  );
}
/* eslint-enable react/prop-types */
